import React, {useEffect} from 'react';

// mui utils
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TableContainer from '@mui/material/TableContainer';
import Container from '@mui/material/Container';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Box from '@mui/material/Box';

// third party utils
import { useSelector,useDispatch } from 'react-redux';

import cloudinary from 'cloudinary-core';
import moment from 'moment';

// iangels utils

import {getDocumentTypesAttempt } from './actions';
import { getAuthResultSelector } from '../App/selectors';
import { getDocumentTypesSelector } from './selectors';

import { loadConfig } from '../..//env/config_util';
import Text from '../../components/text';
import IsMobileDevice from '../../components/Generic/IsMobileDevice';
import { createHttpClient } from '../../services/http_request';

const SharedInfo = ({sharedBy, sharedWith, sharedAllWith }) => {
  return (
    <>
    {
      sharedBy || sharedAllWith ? (
        <div style={{ marginBottom: 5 , padding: 15}} className="gray-bg g4-c f-f-g bold f-size-14 ribbon-sector">
        {sharedBy ? 'Shared by' : 'Documents shared with'}: {sharedBy || sharedAllWith}
      </div>
      ) :
      sharedWith ? (
        <div>
          <br className="toPdf hidden" />
          <Text tx="portfolio.shared.sharedWith" afterText={sharedWith} classes="g-c f-size-12"
            afterClasses="f-size-12 underline b-c"/>
        </div>
      ) :
      null
      }
    </>
  );
};

const DocumentsTable = ({ filteredDocuments, matches }) => {
  //hooks
  const isMobile = IsMobileDevice();
  const config = loadConfig();
  const authResult = useSelector(getAuthResultSelector);
  const documentTypesMap = useSelector(getDocumentTypesSelector);
  const dispatch = useDispatch();
  const safeFileName = (fileName) => {
    return (fileName || '')
      .toString()
      .replace(/[/\\]/g, '_')
      .replace(/\s+/g, ' ')
      .trim();
  };
  const getMomentYear = (date) => {
    if (!date) return null;
    if (typeof date.year === 'function') return date.year();
    if (typeof date.getFullYear === 'function') return date.getFullYear();
    if (typeof date.year === 'number') return date.year;
    return null;
  };
  const getMomentQuarter = (date) => {
    if (!date) return null;
    if (typeof date.quarter === 'function') return date.quarter();
    return null;
  };
  // Keep it aligned with backend helpers (doc_file_name / document_file_name style)
  const buildDownloadFileName = (fileElement, extension = 'pdf') => {
    const entity = safeFileName(fileElement?.entity || fileElement?.startup || 'document');
    const round = safeFileName(fileElement?.roundName || '');
    const type = fileElement?.type ? fileElement.type.toString() : '';
    const date = fileElement?.dateCreated;
    const year = getMomentYear(date);
    const quarter = getMomentQuarter(date);
    const capType = type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Document';
    let baseName;
    if (type === 'k1' || type === ':k1') {
      baseName = `${entity}_${round}_K1_${year || ''}`;
    } else if (type === 'financial_statements_venture' || type === 'capital_statements_venture') {
      baseName = `${entity}_${round}_${capType}_Q${quarter || 1}_${year || ''}`;
    } else if (year) {
      baseName = `${entity}_${round}_${capType}_${year}`;
    } else {
      baseName = `${entity}_${round}_${capType}`;
    }
    const normalizedBase = safeFileName(baseName) || 'document';
    return `${normalizedBase}.${extension}`;
  };
  const getUrlByType = (url, groupType, isCloudinary) => {
    if (!url) {
      return;
    }
    if (isCloudinary) {
      return cloudinary.Cloudinary.new({
        cloud_name: config.config_module.cloudinaryCloudName,
      }).url(url);
    }
    // OWASP A01:2021 - No token in URL. Use Authorization header via axios.
    return url;
  };

  const fetchDocumentWithAuth = (fullUrl) => {
    const api = createHttpClient();
    const path = new URL(fullUrl).pathname;
    return api.get(path, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${authResult?.__raw}` },
    });
  };

  const viewDocUrl = (fileElement) => {
    const viewLink = !fileElement.missingDocument
      ? fileElement.url && getUrlByType(fileElement.url, fileElement.type, fileElement.isCloudinary)
      : fileElement.templateUrl &&
      getUrlByType(fileElement.templateUrl, fileElement.type, fileElement.templateIsCloudinary);

    if (!viewLink) return;

    const isCloudinaryUrl = !fileElement.missingDocument ? fileElement.isCloudinary : fileElement.templateIsCloudinary;
    const openBlob = (blob) => window.open(URL.createObjectURL(blob));

    if (isCloudinaryUrl) {
      fetch(viewLink)
        .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
        .then(openBlob)
        .catch(() => {});
    } else {
      fetchDocumentWithAuth(viewLink)
        .then((r) => openBlob(r.data))
        .catch(() => {});
    }
  };

  const downloadDocUrl = (fileElement) => {
    const downloadLink = !fileElement.missingDocument
      ? fileElement.downloadUrl && getUrlByType(fileElement.downloadUrl, fileElement.type, fileElement.isCloudinary)
      : fileElement.templateDownloadUrl &&
      getUrlByType(fileElement.templateDownloadUrl, fileElement.type, fileElement.templateIsCloudinary);

    if (!downloadLink) return;

    const isCloudinaryUrl = !fileElement.missingDocument ? fileElement.isCloudinary : fileElement.templateIsCloudinary;

    const downloadFromBlob = (blob) => {
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = buildDownloadFileName(fileElement, 'pdf');
      link.click();
      URL.revokeObjectURL(fileURL);
    };

    if (isCloudinaryUrl) {
      // Cloudinary signed URLs are fetchable directly (no Authorization header needed in our frontend).
      fetch(downloadLink)
        .then(async (response) => {
          if (!response.ok) {
            const contentType = response.headers.get('content-type') || '';
            const isUnauthorized = response.status === 401 || response.statusText === 'Unauthorized';
            let backendMsg = null;

            if (contentType.includes('application/json')) {
              try {
                const data = await response.json();
                backendMsg = data?.error?.message || data?.message || null;
              } catch (e) {
                backendMsg = null;
              }
            } else {
              try {
                backendMsg = await response.text();
              } catch (e) {
                backendMsg = null;
              }
            }

            if (isUnauthorized) {
              const msg =  'Authorization time passed. Please refresh the page and try again.';
              const authErr = new Error(msg);
              authErr.code = 'AUTH_EXPIRED';
              throw authErr;
            }

            throw new Error(backendMsg || `Download failed (${response.status})`);
          }

          return response.blob();
        })
        .then(downloadFromBlob)
        .catch((err) => {
          const msg = err?.message || 'Download failed';
          if (err?.code === 'AUTH_EXPIRED') window.alert(msg);
          // eslint-disable-next-line no-console
          console.error('Download error:', err);
        });
    } else {
      // Private URLs: use axios with Authorization header.
      fetchDocumentWithAuth(downloadLink)
        .then((res) => {
          downloadFromBlob(res.data);
        })
        .catch(() => {});
    }
  };

  const isPending = (row) => {
    return row.isPending;
  }

  const getRowDate = (row) => {
    if (!documentTypesMap || !row.dateCreated) return null;
    switch(documentTypesMap?.find((d)=>d.name === row.type)?.periodInDocumentsCenter)
    {
      case 'none':
        return '';
      case 'monthly':
        return moment(row.dateCreated).format('MMM, YYYY');
      case 'quarterly':
          return 'Q' + moment(row.dateCreated).quarter() + ', ' + moment(row.dateCreated).format('YYYY')
      case 'yearly':
        return moment(row.dateCreated).format('YYYY');
      default:
        return moment(row.dateCreated).format('MMM, YYYY');
    }
  };

  useEffect(() => {
    dispatch(getDocumentTypesAttempt());
 }, [dispatch]);

  return (
    <Container
      style={{
        padding: 0,
        marginTop: 40,
        width: '100%',
        minHeight: 800,
      }}
    >
      <Box>
      
      {Object.entries(filteredDocuments).map(([sharedBy, documentsByType], sharedByIndex) => (
      <div key={sharedByIndex} style={{ marginBottom: 25 }}>
        {/* Add header for shared_by */}                        
        {sharedBy !== "null" ? <SharedInfo sharedBy={sharedBy} />: <SharedInfo sharedAllWith={Object.values(documentsByType).flat().find(item => item.sharedAllWith)?.sharedAllWith} />}
        {/* Iterate over each document type within the current shared_by group */}
        {Object.entries(documentsByType).map(([type, documents], typeIndex) => (
          <div style={{ marginBottom: 25 }} key={typeIndex}>
            <TableContainer component={Paper} style={{ borderRadius: 0, borderTop: '#4CBABB solid 3px' }}>
              <div style={{ padding: 15 }}>
                <Text classes="f-f-tgf f-w-500 g4-c f-size-16" text={documentTypesMap?.find((d)=>d.name === type)?.description || type} />
              </div>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow className="g1-bg">
                    <TableCell width="20%" className="f-w-700">
                      Company Name
                      {isMobile && <div>Round Name</div>}
                    </TableCell>
                    {!isMobile && (
                      <TableCell width="20%" className="f-w-700">
                        Round Name
                      </TableCell>
                    )}
                    <TableCell width="20%" className="f-w-700" style={{ verticalAlign: 'top' }}>
                      Entity
                      {isMobile && (
                        <div>
                          <br />
                          Date
                        </div>
                      )}
                    </TableCell>
                    {!isMobile && (
                      <TableCell width="20%" className="f-w-700">
                        Date
                      </TableCell>
                    )}
                    <TableCell width="10%"></TableCell>
                    <TableCell width="10%"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((row, rowIndex) => (
                  <TableRow key={rowIndex} sx={{ '&:last-child td, &:last-child th': { border: 0 } }} className={`f-f-g ${isPending(row) ? ' gr-bg' : ''}`}>
                    <TableCell className="f-f-tgf f-w-500 f-size-12 g4-c">
                        {row.startup}
                        {isMobile && <div className="f-f-g f-w-400 f-size-12">{row.roundName}</div> }
                    </TableCell>
                    {!isMobile && <TableCell className="f-f-g f-w-400 f-size-12">{row.roundName}</TableCell>}
                    <TableCell className="f-f-g f-w-400 f-size-12">
                        {row.entity} {isMobile && <div>{getRowDate(row)}</div>}
                        <SharedInfo sharedWith={row.sharedWith} />
                    </TableCell>
                    {!isMobile && <TableCell className="f-f-g f-w-400 f-size-12">{getRowDate(row)}</TableCell>}
                    <TableCell colspan={isPending(row) ? 2 : null}>
                        {isPending(row) ? `${documentTypesMap?.find((d)=>d.name === type)?.description} not yet uploaded` : <Button
                          variant="text"
                          style={{ color: 'black', minWidth: 10, padding: 0 }}
                          startIcon={<VisibilityIcon className="g-c" />}
                          onClick={() => viewDocUrl(row)}
                        >
                          {!isMobile && <span>VIEW</span>}
                        </Button>}
                      </TableCell>
                      {isPending(row) ? null : <TableCell>
                        <Button
                          variant="text"
                          style={{ color: 'black' }}
                          startIcon={<FileDownloadIcon className="g-c" />}
                          onClick={() => downloadDocUrl(row)}
                        >
                          {!isMobile && <span>DOWNLOAD</span>}
                        </Button>
                      </TableCell>}
                    </TableRow>
                  ))} 
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        ))}
        </div>
))}
      </Box>
    </Container>
  );
};

export default DocumentsTable;
